import { cn } from '@/lib/utils';

export type KeyState = 'green' | 'yellow' | 'gray' | 'unused';

interface KeyboardProps {
  letterStates: Record<string, KeyState>;
  onKeyPress: (key: string) => void;
  onEnter: () => void;
  onBackspace: () => void;
  disabled?: boolean;
}

const ROWS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['enter', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace'],
];

const keyStateClasses: Record<KeyState, string> = {
  green: 'bg-green-600 text-white hover:bg-green-700',
  yellow: 'bg-yellow-500 text-white hover:bg-yellow-600',
  gray: 'bg-zinc-500 text-white hover:bg-zinc-600',
  unused: 'bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600',
};

export function Keyboard({
  letterStates,
  onKeyPress,
  onEnter,
  onBackspace,
  disabled = false,
}: KeyboardProps) {
  const handleClick = (key: string) => {
    if (disabled) return;
    if (key === 'enter') {
      onEnter();
    } else if (key === 'backspace') {
      onBackspace();
    } else {
      onKeyPress(key);
    }
  };

  return (
    <div className="flex flex-col gap-1 items-center" data-testid="keyboard">
      {ROWS.map((row, rowIdx) => (
        <div key={rowIdx} className="flex gap-1">
          {row.map((key) => {
            const state = letterStates[key] || 'unused';
            const isSpecial = key === 'enter' || key === 'backspace';
            const label = key === 'backspace' ? '⌫' : key.toUpperCase();

            return (
              <button
                key={key}
                onClick={() => handleClick(key)}
                disabled={disabled}
                className={cn(
                  'h-14 rounded font-bold text-sm flex items-center justify-center transition-colors',
                  isSpecial ? 'px-3 min-w-[65px]' : 'w-9',
                  isSpecial
                    ? 'bg-zinc-300 dark:bg-zinc-600 hover:bg-zinc-400 dark:hover:bg-zinc-500'
                    : keyStateClasses[state],
                  disabled && 'opacity-50 cursor-not-allowed',
                )}
                data-testid={`key-${key}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
